/**
 * ScenarioFormModal — wraps the shared FormModal with the Step 3
 * damage-scenario field schema. Drives both Add and Edit flows.
 *
 * The `assetOptions` prop is injected by the consumer (`useStep3` →
 * Step3DamageScenarios) since the asset list is session-specific.
 * The schema's `asset_ids` field declares an empty options array;
 * this component patches it with the live list before rendering.
 */
import React, { useMemo } from 'react';
import FormModal from '../../../../components/ui/FormModal/FormModal';
import { SCENARIO_FORM_FIELDS, type ScenarioRecord } from './scenarioFieldSchema';

const FormModalAny = FormModal as unknown as React.ComponentType<any>;

export interface AssetOption {
  value: string;
  label: string;
}

export interface ScenarioFormModalProps {
  mode: 'add' | 'edit';
  initialScenario?: ScenarioRecord | null;
  assetOptions: AssetOption[];
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

export default function ScenarioFormModal({
  mode,
  initialScenario,
  assetOptions,
  onSubmit,
  onClose,
}: ScenarioFormModalProps) {
  const isEdit = mode === 'edit';

  const fields = useMemo(
    () =>
      SCENARIO_FORM_FIELDS.map((f) => {
        if (f.name === 'asset_ids') return { ...f, options: assetOptions };
        if (isEdit && f.name === 'damage_id') return { ...f, disabled: true };
        return f;
      }),
    [assetOptions, isEdit],
  );

  return (
    <FormModalAny
      title={isEdit ? 'Edit Damage Scenario' : 'Add Damage Scenario'}
      fields={fields}
      initialValues={initialScenario ?? {}}
      submitLabel={isEdit ? 'Save Changes' : 'Add Scenario'}
      submittingLabel={isEdit ? 'Saving…' : 'Adding…'}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth={720}
    />
  );
}
